import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import './UnitSummary.css';
import DateIcon from '../DateIcon/DateIcon';
import LargeNumberIcon from '../LargeNumberIcon/LargeNumberIcon';
import { format } from 'date-fns';
import baseApi from '../../common/api/base';
import StaffCheckIn from '../StaffCheckIn/StaffCheckIn';
import DefaultMonitoredIndividualIcon from '../DefaultMonitoredIndividualIcon/DefaultMonitoredIndividualIcon';
import CardContainer from '../CardContainer/CardContainer';
import ModuleLoadingStatus from '../ModuleLoadingStatus/ModuleLoadingStatus';
import { EnvironmentContext } from '../../contexts/EnvironmentContext';
import logComponent from '../../common/logComponent';

export default function UnitSummary({ close: propsClose }) {
  const environmentContext = useContext(EnvironmentContext);
  const [exiting, setExiting] = useState(false);
  const [visibleDateIndex, setVisibleDateIndex] = useState(0);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState(false);
  const [data, setData] = useState(null);
  const [checkInInProgress, setCheckInInProgress] = useState(null);
  const { selectedUnit, organization } = environmentContext;
  const { unitId: routedUnitId } = useParams();
  const { hasToken } = environmentContext;

  if (logComponent('UnitSummary')) { console.debug('UnitSummary rendered'); }

  useEffect(() => {
      if (logComponent('UnitSummary')) { console.debug('UnitSummary onMount'); }

  }, [])

  useEffect(() => {
    if (logComponent('UnitSummary')) { console.debug('UnitSummary useEffect [environmentContext.selectedMonitoredIndividual]'); }
    loadData();
  }, [environmentContext.selectedMonitoredIndividual])

  useEffect(() => {
    if (logComponent('UnitSummary')) { console.debug('UnitSummary useEffect [environmentContext.selectedUnit]'); }
    loadData();
  }, [environmentContext.selectedUnit])

  const loadData = () => {
    if (logComponent('UnitSummary')) { console.debug('UnitSummary loadData'); }
    if (!hasToken) return;

    setVisibleDateIndex(0);
    setData(null);
    setDataLoading(true);
    setDataError(false);

    let unitIdToLoad;

    if (selectedUnit.unitId !== routedUnitId) {
      unitIdToLoad = routedUnitId;

    }

    baseApi.Unit.GetSummaryForPastDays(unitIdToLoad, 7)
      .then(
        result => {
          let prevData = (data ? data : {});
          setData(Object.assign(prevData, result.data));
          setDataLoading(false);
          setDataError(false);
        },
        error => {
          setDataLoading(false);
          setDataError(true);
        }
      )
  }

  const handleDailyNoteIconClick = (e) => {
    if (logComponent('UnitSummary')) { console.debug('UnitSummary handleDailyNoteIconClick'); }
    const { noteIndex } = e.currentTarget.dataset;

    if (noteIndex) selectDate(parseInt(noteIndex, 10));
  }

  const handleAnimationEnd = (e) => {
    if (logComponent('UnitSummary')) { console.debug('UnitSummary handleAnimationEnd'); }
    if (e.target.matches('.Unit-Summary--exiting')) propsClose();
  }

  const selectDate = (newDateIndex) => {
    if (logComponent('UnitSummary')) { console.debug('UnitSummary selectDate'); }
    setVisibleDateIndex(newDateIndex);
  }

  const getDailyNoteSummary = () => {
    if (logComponent('UnitSummary')) { console.debug('UnitSummary getDailyNoteSummary'); }
    const numberOfNotes = data.noteDays[visibleDateIndex].noteCount;

    if (numberOfNotes === 1) {
      return <p>There is <span className="bold">1</span> check in note available for this day.</p>;
    } else {
      return <p>There are <span className="bold">{numberOfNotes}</span> check in notes available for this day.</p>;
    }
  }

  const getNotesCount = () => {
    if (logComponent('UnitSummary')) { console.debug('UnitSummary getNotesCount'); }

    if (!data || !data.noteDays) return 0;

    return data.noteDays.reduce((prev, curr) => {
      return prev + curr.notes.length;
    }, 0);
  }

  const close = () => {
    if (logComponent('UnitSummary')) { console.debug('UnitSummary close'); }
    setExiting(true);
  }

  const startCheckIn = () => {
    if (logComponent('UnitSummary')) { console.debug('UnitSummary startCheckIn'); }
    setCheckInInProgress(true);
  }

  const endCheckIn = () => {
    if (logComponent('UnitSummary')) { console.debug('UnitSummary endCheckIn'); }
    setCheckInInProgress(false);
    loadData();
  }

  return (
    <div
      className={'Unit-Summary' + (exiting ? ' Unit-Summary--exiting' : '')}
      onAnimationEnd={handleAnimationEnd}>
      <div className="Unit-Summary__main">
        <p className="Unit-Summary__heading">Unit Summary</p>
        {
          data &&
          <div className="Unit-Summary__content">
            <div className="Unit-Summary__top-row">
              <div className="Unit-Summary__bio Unit-Summary__top-row-item">
                <div className="Unit-Summary__monitoredindividual-info">
                  <div className="Unit-Summary__monitoredindividual-image">
                      <DefaultMonitoredIndividualIcon />
                  </div>
                  <p className="Unit-Summary__unit-name"> {selectedUnit.name}</p>
                </div>
                <div className="Unit-Summary__facility-info">
                  {organization.name}
                  </div>
              {/*  TODO:  What do we want to do with Units Checking in*/}
              {/*  <div className="Unit-Summary__check-in-area--md">*/}
              {/*    <button*/}
              {/*      className="Unit-Summary__check-in-button"*/}
              {/*      onClick={startCheckIn}>Check in*/}
              {/*    </button>*/}
              {/*  </div>*/}
              </div>
              <div className="Unit-Summary__past-week Unit-Summary__top-row-item">
                <p className="Unit-Summary__past-week-heading">This past week</p>
                <div className="Unit-Summary__past-week-content">
                  <table className="Unit-Summary__past-week-content__table">
                    <thead className="Unit-Summary__past-week-content__thead">
                      <tr key="header">
                        <th className="Unit-Summary__past-week-content__name-heading">Unit / Individual</th>
                        <th className="Unit-Summary__past-week-content__check-in-heading">Check-ins</th>
                        <th className="Unit-Summary__past-week-content__notes-heading">Notes</th>
                        <th className="Unit-Summary__past-week-content__alarms-heading">Alarms</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr key="unit" className="Unit-Summary__past-week-content__row">
                        <td className="Unit-Summary__past-week-content__row__name-cell">{data.name}</td>
                        <td className="Unit-Summary__past-week-content__row__check-in-cell">{data.checkInCount}</td>
                        <td className="Unit-Summary__past-week-content__row__notes-cell">{data.noteCount}</td>
                        <td className="Unit-Summary__past-week-content__row__alarms-cell"> {data.alarmsCount}</td>
                      </tr>
                      {
                        data.relatedMonitoredIndividualSummaries &&
                        data.relatedMonitoredIndividualSummaries
                          .map(summary =>
                            <tr className="Unit-Summary__past-week-content__row"
                              key={summary.MonitoredIndividualId}>
                              <td className="Unit-Summary__past-week-content__row__name-cell-link"><Link to={`/monitoredindividual/${summary.monitoredIndividualId}`}>{`${summary.legalFirstName} ${summary.lastName}`}</Link></td>
                              <td className="Unit-Summary__past-week-content__row__check-in-cell">{summary.checkInCount}</td>
                              <td className="Unit-Summary__past-week-content__row__notes-cell">{summary.noteCount}</td>
                              <td className="Unit-Summary__past-week-content__row__alarms-cell">{summary.alarmsCount}</td>

                            </tr>

                          )
                      }
                    </tbody>
                  </table>


                </div>
              </div>
            {/*  TODO:  What do we want to do with Units Checking in */}
            {/*  <div className="Unit-Summary__check-in-area Unit-Summary__top-row-item">*/}
            {/*    <button*/}
            {/*      className="Unit-Summary__check-in-button"*/}
            {/*      onClick={startCheckIn}>Check in</button>*/}
            {/*  </div>*/}
            </div>
            {
              data.noteDays &&
              data.noteDays.length > 0 &&
              <div className="Unit-Summary__notes">
                <p className="Unit-Summary__notes-heading">Recent notes</p>
                <ul className="Unit-Summary__daily-notes-list">
                  {
                    data.noteDays.map((note, index) =>
                      <li
                        key={'li_' + index.toString()}
                        className="Unit-Summary__daily-notes-item">
                        <button
                          className="Unit-Summary__daily-notes-button"
                          onClick={handleDailyNoteIconClick}
                          data-note-index={index}>
                          <DateIcon date={note.noteOn} size="4rem" darkColor={visibleDateIndex === index ? '#363633' : '#8B8B8B'} lightColor="#fdfdfd" />
                        </button>
                      </li>
                    )
                  }
                </ul>
                {
                  data.noteDays[visibleDateIndex].firstStaffCheckHistoryWithStaffNote &&
                  <React.Fragment>
                    <div className="Unit-Summary__visible-note">
                      <div className="Unit-Summary__visible-note-info">
                        <label>Note info</label>
                        <div className="Unit-Summary__visible-note-info-content">
                          <div className="Unit-Summary__visible-note-info-top">
                                  <p className="Unit-Summary__visible-note-date">{format(data.noteDays[visibleDateIndex].noteOn, 'MMMM d, yyyy')}</p>
                            <p className="Unit-Summary__visible-note-dot">{' \u2022 '}</p>
                            <p className="Unit-Summary__visible-note-time">{format(data.noteDays[visibleDateIndex].firstStaffCheckHistoryWithStaffNote.localCheckInOn, 'h:mm aaa')}</p>
                          </div>
                          <p className="Unit-Summary__visible-note-author">{`${data.noteDays[visibleDateIndex].firstStaffCheckHistoryWithStaffNote.user.legalFirstName} ${data.noteDays[visibleDateIndex].firstStaffCheckHistoryWithStaffNote.user.lastName}`}</p>
                        </div>
                      </div>
                      <div className="Unit-Summary__visible-note-content">
                        <label>Comments</label>
                        <p className="Unit-Summary__visible-note-content-text">{data.noteDays[visibleDateIndex].firstStaffCheckHistoryWithStaffNote.staffNote.body}</p>
                      </div>
                    </div>
                  </React.Fragment>
                }
                <div className="Unit-Summary__daily-note-summary">
                  {getDailyNoteSummary()}
                  {/* TODO: Add link to unit/:unitId/notes, once NotesHistory is appropriately updated to handle notes for units */}

                  <Link
                    className="Unit-Summary__note-history-link"
                    to={`/unit/${selectedUnit.unitId}/notes`}>See full notes history</Link>

                </div>
              </div>
            }
          </div>

        }
        <ModuleLoadingStatus isLoading={dataLoading} isError={dataError} reloadAction={loadData} />
      </div>
      <CardContainer />
      {
        checkInInProgress &&
        <StaffCheckIn close={endCheckIn} />
      }
    </div>
  );
}
